import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Dispatch, Fragment, memo, useMemo, useState } from "react";

import { EmptyRow } from "@buildpy/ui/empty-state";
import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { cn } from "@/lib/utils";
import { safeCalcTrade } from "@buildpy/core/modules/quote/safe-calc";
import Money from "@/components/money";
import ItemRow from "@/modules/quote/ui/editor/item-row";
import TemplatePicker from "@/modules/quote/ui/editor/template-picker";
import { fieldsetFreePress, lockedNow, NEVER_LOCKED, unlessLockedEdit } from "@/modules/quote/ui/editor/locked-guard";
import { commitMarginText } from "@buildpy/core/modules/quote/editor/sheet/cell-edit";
import { marginEditable } from "@buildpy/core/modules/quote/editor/reducer";
import { isMarginBp } from "@buildpy/core/modules/template/price-margin";
import {
  EditorAction,
  EditorTrade,
  ItemRef,
} from "@buildpy/core/modules/quote/editor/types";
import { TemplateItem } from "@buildpy/core/modules/template/types";
import type { VatMode } from "@buildpy/core/modules/money/vat";

interface Props {
  trade: EditorTrade;
  dispatch: Dispatch<EditorAction>;
  templateItems: TemplateItem[];
  onSuggest: (ref: ItemRef) => void;
  /** 공간 선택지. 항목 줄이 그대로 받아 쓴다. */
  spaces: { code: string; label: string }[];
  /** 간단 모드면 자재·노무 4칸 대신 단가 한 칸만 보인다. */
  simple: boolean;
  /** 수량식의 「평」 값. */
  area: number | null;
  /** 수량식의 「m²」 값 — 없으면 평×3.305785. */
  areaM2?: number | null;
  // 원가·마진 두 열을 합계 뒤에 보일지.
  showCost?: boolean;
  vatMode?: VatMode;
  // 잠겨도 접기는 그대로, 편집 칸만 끈다.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — fieldset 이 꺼지며 나는 blur 복원·늦은 확정을 버린다(잠김 없는 설정 화면은 생략).
  isLocked?: () => boolean;
}

/** 상세 모드의 열. 자재·노무를 나눠 쓰는 회사가 본다. */
const DETAIL_HEADERS = [
  { label: "공간", className: "w-[9%] text-left" },
  { label: "범위", className: "w-[9%] text-left" },
  { label: "작업내역", className: "w-[20%] text-left" },
  { label: "규격", className: "w-[7%] text-left" },
  { label: "수량", className: "w-[6%] text-right" },
  { label: "자재 단가", className: "w-[8%] text-right" },
  { label: "자재 금액", className: "w-[9%] text-right" },
  { label: "노무 단가", className: "w-[8%] text-right" },
  { label: "노무 금액", className: "w-[9%] text-right" },
  { label: "합계", className: "w-[9%] text-right" },
  { label: "비고", className: "w-[9%] text-left" },
  { label: "", className: "w-8 text-left" },
];

/**
 * 간단 모드의 열. 10칸을 5칸으로 줄인다 — 자재·노무를 나눠 쓰는 회사는 소수라,
 * 기본은 합친 단가 하나만 받는다. 범위(부위)도 접고 공간만 남긴다.
 */
const SIMPLE_HEADERS = [
  { label: "공간", className: "w-[12%] text-left" },
  { label: "작업내역", className: "w-[38%] text-left" },
  { label: "규격", className: "w-[9%] text-left" },
  { label: "수량", className: "w-[9%] text-right" },
  { label: "단가", className: "w-[13%] text-right" },
  { label: "합계", className: "w-[13%] text-right" },
  { label: "비고", className: "w-[6%] text-left" },
  { label: "", className: "w-8 text-left" },
];

const COST_HEADERS = [
  { label: "원가", className: "w-[8%] text-right" },
  { label: "마진", className: "w-[7%] text-right" },
];

// 합계 바로 뒤에 원가·마진 열을 끼운다.
const withCostHeaders = (headers: { label: string; className: string }[]) => {
  const at = headers.findIndex((head) => head.label === "합계") + 1;
  return [...headers.slice(0, at), ...COST_HEADERS, ...headers.slice(at)];
};

// 원가 있는 잎(SET 자식 포함)이 하나라도 있는가.
const hasCostLeaf = (trade: EditorTrade): boolean =>
  trade.items.some((item) => (item.children.length > 0 ? item.children.some(marginEditable) : marginEditable(item)));

// 공종 일괄 마진 — 적은 % 를 그 공종의 원가 있는 줄 전부에 한 번에 건다(되돌리기 한 단계).
const TradeMarginControl = ({
  trade,
  dispatch,
  disabled,
}: {
  trade: EditorTrade;
  dispatch: Dispatch<EditorAction>;
  disabled: boolean;
}) => {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const hasCost = hasCostLeaf(trade);
  const enabled = !disabled && hasCost;
  const apply = () => {
    const result = commitMarginText(text);
    if (!result.ok || !isMarginBp(Math.round(result.margin * 10_000))) {
      setError("마진은 -90% ~ +1000% 숫자로 적어 주세요");
      return;
    }
    setError(null);
    setText("");
    dispatch({ type: "SET_TRADE_MARGIN", tradeKey: trade.key, margin: result.margin });
  };
  return (
    <div className="ml-auto flex items-center gap-2" data-testid="trade-margin">
      <Input
        aria-label="공종 일괄 마진 %"
        className="w-24 text-right tabular-nums"
        disabled={!enabled}
        errorMessage={error ?? undefined}
        inputMode="decimal"
        placeholder="마진 %"
        title={error ?? (hasCost ? undefined : "원가를 넣은 줄이 있어야 쓸 수 있습니다")}
        value={text}
        variant="outline"
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") apply();
        }}
      />
      <Button disabled={!enabled || text.trim() === ""} size="sm" type="button" variant="outline" onClick={apply}>
        공종 일괄 마진
      </Button>
    </div>
  );
};

/** 공종 하나. 접으면 금액 줄만 남는다 — 15개를 다 펴면 스크롤이 길어 못 읽는다. */
const TradeCard = ({
  trade,
  dispatch: rawDispatch,
  templateItems,
  onSuggest,
  spaces,
  simple,
  area,
  areaM2,
  showCost = false,
  vatMode = "INCL",
  disabled = false,
  isLocked = NEVER_LOCKED,
}: Props) => {
  // 줄·공종 칸이 보내는 편집은 보내는 순간 잠겨 있으면 버린다(접기는 보낸다).
  const dispatch = useMemo(() => unlessLockedEdit(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  const headers = useMemo(() => {
    const base = simple ? SIMPLE_HEADERS : DETAIL_HEADERS;
    return showCost ? withCostHeaders(base) : base;
  }, [simple, showCost]);

  // **던지지 않는 쪽을 쓴다.** 렌더 중에 계산하므로 여기서 던지면 편집기가 통째로
  // 언마운트되어 미저장 편집이 사라진다 — 자릿수를 잘못 친 순간 화면이 날아간다.
  const amounts = useMemo(
    () => safeCalcTrade({ group: trade.group, items: trade.items }),
    [trade.group, trade.items]
  );

  const collapsed = trade.collapsed;

  return (
    <SectionCard padding="none">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
        {/* 접기는 보기 전용 — 날 button 이 아닌 span 이라 잠긴 본문 fieldset 안에서도 눌린다. */}
        <Button aria-expanded={!collapsed} aria-label={collapsed ? "펼치기" : "접기"} size="icon-xs" variant="ghost-muted" asChild>
          <span {...fieldsetFreePress(() => dispatch({ type: "TOGGLE_TRADE", tradeKey: trade.key }))}>
            {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
          </span>
        </Button>

        <Input
          aria-label="공종명"
          className="w-auto min-w-32 flex-1 text-lg font-medium"
          disabled={disabled}
          value={trade.name}
          variant="cell"
          onChange={(e) =>
            dispatch({
              type: "SET_TRADE_NAME",
              tradeKey: trade.key,
              value: e.target.value,
            })
          }
        />

        <span className="whitespace-nowrap text-sm text-muted-foreground">
          자재 <Money value={amounts.material_amount} /> · 노무 <Money value={amounts.labor_amount} />
        </span>
        <Money className="w-32 text-right font-semibold" value={amounts.amount} />

        <Button
          aria-label="공종 삭제"
          disabled={disabled}
          size="icon-xs"
          type="button"
          variant="ghost-destructive"
          onClick={() =>
            dispatch({ type: "REMOVE_TRADE", tradeKey: trade.key })
          }>
          <Trash2 className="size-4" />
        </Button>
      </header>

      {!collapsed && (
        <div className="border-t border-border px-3 py-2">
          <div className="overflow-x-auto">
            <table className={cn("w-full table-fixed", showCost ? "min-w-[1120px]" : "min-w-[960px]")}>
              <thead>
                <tr className="border-b border-border">
                  {headers.map((head, index) => (
                    <th
                      key={head.label || `col-${index}`}
                      className={cn(
                        "px-1.5 py-1 text-xs font-medium text-muted-foreground",
                        head.className
                      )}>
                      {head.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trade.items.length === 0 && (
                  <EmptyRow colSpan={headers.length}>항목이 없습니다.</EmptyRow>
                )}
                {trade.items.map((item) => (
                  <Fragment key={item.key}>
                    <ItemRow
                      disabled={disabled}
                      dispatch={dispatch}
                      item={item}
                      parentKey={null}
                      tradeKey={trade.key}
                      onSuggest={onSuggest}
                    area={area}
                    areaM2={areaM2}
                    showCost={showCost}
                    simple={simple}
                    spaces={spaces}
                    vatMode={vatMode}
                    />
                    {item.children.map((child) => (
                      <ItemRow
                        key={child.key}
                        disabled={disabled}
                        dispatch={dispatch}
                        item={child}
                        parentKey={item.key}
                        tradeKey={trade.key}
                        onSuggest={onSuggest}
                    area={area}
                    areaM2={areaM2}
                    showCost={showCost}
                    simple={simple}
                    spaces={spaces}
                    vatMode={vatMode}
                      />
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              disabled={disabled}
              size="sm"
              variant="outline"
              onClick={() => dispatch({ type: "ADD_ITEM", tradeKey: trade.key })}>
              <Plus className="size-4" /> 항목
            </Button>
            <TemplatePicker
              disabled={disabled}
              isLocked={isLocked}
              items={templateItems}
              tradeName={trade.name}
              onAdd={(items) =>
                dispatch({
                  type: "ADD_TEMPLATE_ITEMS",
                  tradeKey: trade.key,
                  items,
                })
              }
            />
            {showCost && <TradeMarginControl disabled={disabled} dispatch={dispatch} trade={trade} />}
          </div>

          <Field className="mt-3" label="조건 문구">
            <Textarea
              className="min-h-16 text-sm"
              disabled={disabled}
              value={trade.condition_text}
              onChange={(e) =>
                dispatch({
                  type: "SET_TRADE_CONDITION",
                  tradeKey: trade.key,
                  value: e.target.value,
                })
              }
            />
          </Field>
        </div>
      )}
    </SectionCard>
  );
};

export default memo(TradeCard);
